// src/app/specialists/[slug]/components/SpecialistBiography.tsx
'use client';

import { useEffect, useRef, useState } from 'react';

import RichText from '@/components/ui/RichText/RichText';

interface SpecialistBiographyProps {
  text?: string | null;
  title?: string;
  styles?: Record<string, string>;
}

export default function SpecialistBiography({
  text,
  title = 'Biography',
  styles,
}: SpecialistBiographyProps) {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const cleanText = text?.trim();

  useEffect(() => {
    const contentNode = contentRef.current;

    if (!contentNode || !cleanText) {
      setShowToggle(false);
      return undefined;
    }

    const updateShowToggle = () => {
      if (expanded) {
        setShowToggle(true);
        return;
      }

      const canOverflow = contentNode.scrollHeight > contentNode.clientHeight;
      setShowToggle(canOverflow);
    };

    updateShowToggle();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateShowToggle)
        : null;

    resizeObserver?.observe(contentNode);

    window.addEventListener('resize', updateShowToggle);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateShowToggle);
    };
  }, [cleanText, expanded]);

  if (!cleanText) {
    return null;
  }

  return (
    <section className="mb-8 w-full">
      <h2 className="text-theme-primary mb-4 text-base font-bold">{title}</h2>

      <div
        ref={contentRef}
        className={`text-gray-cool-text overflow-hidden text-sm leading-relaxed font-normal transition-all duration-300 sm:text-base ${
          !expanded ? 'line-clamp-6' : ''
        }`}
        style={!expanded ? { WebkitLineClamp: 6 } : undefined}
      >
        <RichText
          styles={{
            p: 'text-gray-cool-text text-sm leading-relaxed font-normal whitespace-pre-line mb-4 block sm:text-base last:mb-0',
            a:
              styles?.a ??
              'text-theme-primary font-semibold underline underline-offset-2 hover:opacity-80',
            ...styles,
          }}
        >
          {cleanText}
        </RichText>
      </div>

      {showToggle && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="text-theme-primary mt-3 cursor-pointer text-sm font-semibold hover:underline focus:outline-none"
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Learn more..'}
        </button>
      )}
    </section>
  );
}
